import { handleReturn, QueryFunction } from '@query/root.ts'
import { MutationFunction } from '@tanstack/react-query'
import { network } from '@utils'

export interface ISalaxySessionResponse {
  access_token: string
  is_enabled: boolean
  is_verified: boolean
}

interface SalaxyTokenProps {
  slug: string
}

export const fetchSalaxySession: QueryFunction<SalaxyTokenProps, ISalaxySessionResponse> = async ({
  slug
}) => {
  const url = `/v1/integrations/salary/${slug}/session/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}

export const enableSalaxy: MutationFunction<void, SalaxyTokenProps> = async ({ slug }) => {
  const url = `/v1/integrations/salary/${slug}/enable/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}

export const disableSalaxy: MutationFunction<void, SalaxyTokenProps> = async ({ slug }) => {
  const url = `/v1/integrations/salary/${slug}/disable/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}

export interface ISalaxyVerifyResponse {
  is_verified: boolean
}

export const verifySalaxy: MutationFunction<ISalaxyVerifyResponse, SalaxyTokenProps> = async ({
  slug
}) => {
  const url = `/v1/integrations/salary/${slug}/verify/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}

export interface ISalaxySettings {
  has_accrual_based_salaries: boolean
  has_salaxy_holiday_accruals: boolean
}

export interface ISalaxyAccountMapping {
  salaxy_account_number: string
  salaxy_account_name: string
  account_id: number | null
  resolved_account_id: number | null
  is_explicit: boolean
}

export const fetchSalaxySettings: QueryFunction<SalaxyTokenProps, ISalaxySettings> = async ({
  slug
}) => {
  const url = `/v1/integrations/salary/${slug}/settings/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}

export const updateSalaxySettings = async ({
  slug,
  ...payload
}: SalaxyTokenProps & Partial<ISalaxySettings>): Promise<ISalaxySettings> => {
  const url = `/v1/integrations/salary/${slug}/settings/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}

export const fetchSalaxyAccountMappings: QueryFunction<
  SalaxyTokenProps,
  ISalaxyAccountMapping[]
> = async ({ slug }) => {
  const url = `/v1/integrations/salary/${slug}/account-mappings/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}

export const replaceSalaxyAccountMappings = async ({
  slug,
  mappings
}: SalaxyTokenProps & {
  mappings: Array<{ salaxy_account_number: string; account_id: number | null }>
}): Promise<ISalaxyAccountMapping[]> => {
  const url = `/v1/integrations/salary/${slug}/account-mappings/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PUT',
    data: mappings,
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}
